(() => {
  'use strict';
  if (customElements.get('fs-live-purchase')) return;
  const appOptions = '.essential-upsell-addon-container, [data-essential-upsell-element], [data-essential-upsell], [id*="essential-upsell"], [class*="essential-upsell"]';
  const inheritedSticky = '[data-essential-sticky-cart], [id*="essential-sticky"], [class*="essential-sticky"], essential-sticky-cart, sticky-add-to-cart, [data-sticky-add-to-cart]';
  const writeText = (node, text) => { if (node && node.textContent !== text) node.textContent = text; };

  class FsLivePurchase extends HTMLElement {
    connectedCallback() {
      if (this.connected) return;
      this.root = this.closest('product-info');
      if (!this.root) return;
      this.connected = true;
      this.bar = this.querySelector('[data-fs-live-bar]');
      this.action = this.querySelector('[data-fs-live-action]');
      this.edit = this.querySelector('[data-fs-live-edit]');
      this.choice = this.querySelector('[data-fs-live-choice]');
      this.price = this.querySelector('[data-fs-live-price]');
      this.status = this.querySelector('[data-fs-live-status]');
      this.confirmed = new Map();
      this.explicitURL = this.dataset.selectedVariantId || '';
      this.storageKey = 'fs-live-purchase:' + this.dataset.productId;
      try { this.uncertain = Boolean(sessionStorage.getItem(this.storageKey)); } catch (_) { this.uncertain = false; }
      this.mobile = window.matchMedia('(max-width: 749px)');
      this.onAction = () => this.activate();
      this.onEdit = () => this.review();
      this.onInteraction = event => this.interact(event);
      this.onRefresh = () => this.queueSync();
      this.action.addEventListener('click', this.onAction);
      this.edit.addEventListener('click', this.onEdit);
      this.root.addEventListener('click', this.onInteraction, true);
      this.root.addEventListener('change', this.onInteraction, true);
      this.root.addEventListener('input', this.onInteraction, true);
      this.mobile.addEventListener('change', this.onRefresh);
      window.addEventListener('scroll', this.onRefresh, { passive: true });
      window.addEventListener('resize', this.onRefresh, { passive: true });
      document.addEventListener('toggle', this.onRefresh, true);
      document.addEventListener('focusin', this.onRefresh);
      this.mutations = new MutationObserver(records => {
        if (records.some(record => !this.contains(record.target))) this.queueSync();
      });
      this.mutations.observe(document.body, { childList: true, subtree: true, attributes: true, attributeFilter: ['disabled', 'aria-disabled', 'class', 'style', 'hidden', 'open', 'value'] });
      if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
        this.unsubscribe = subscribe(PUB_SUB_EVENTS.variantChange, event => {
          if (String(event.data?.sectionId) === this.dataset.sectionId) this.queueSync();
        });
      }
      this.sync();
    }

    disconnectedCallback() {
      this.connected = false;
      this.action?.removeEventListener('click', this.onAction);
      this.edit?.removeEventListener('click', this.onEdit);
      this.root?.removeEventListener('click', this.onInteraction, true);
      this.root?.removeEventListener('change', this.onInteraction, true);
      this.root?.removeEventListener('input', this.onInteraction, true);
      this.mobile?.removeEventListener('change', this.onRefresh);
      window.removeEventListener('scroll', this.onRefresh);
      window.removeEventListener('resize', this.onRefresh);
      document.removeEventListener('toggle', this.onRefresh, true);
      document.removeEventListener('focusin', this.onRefresh);
      this.mutations?.disconnect();
      this.intersection?.disconnect();
      this.unsubscribe?.();
      if (this.frame) cancelAnimationFrame(this.frame);
    }

    queueSync() {
      if (this.frame) return;
      this.frame = requestAnimationFrame(() => { this.frame = null; if (this.connected) this.sync(); });
    }

    groups() { return Array.from(this.root.querySelectorAll('variant-selects .product-form__input')); }
    value(group) { return group.querySelector('input[type="radio"]:checked')?.value || group.querySelector('select')?.value || ''; }
    variant() {
      const picker = this.root.querySelector('variant-selects');
      try { return JSON.parse(picker ? picker.querySelector('[data-selected-variant]')?.textContent || 'null' : this.dataset.defaultVariant || 'null'); } catch (_) { return null; }
    }

    interact(event) {
      const target = event.target;
      if (!event.isTrusted || !target?.closest || this.contains(target)) return;
      const group = target.closest('variant-selects .product-form__input');
      if (group && (event.type === 'change' || (event.type === 'click' && target.matches('input[type="radio"]')))) {
        const index = this.groups().indexOf(group);
        this.confirmed.set(index, this.value(group));
        this.explicitURL = '';
        this.added = false;
        this.error = '';
      }
      if (target.closest(appOptions) && target.closest('input, select, textarea, button, label, [role="checkbox"], [role="option"]')) this.appTouched = true;
      if (event.type !== 'click' && target.matches('input, select, textarea')) {
        this.added = false;
        this.error = '';
      }
      this.queueSync();
    }

    bindForm() {
      const forms = Array.from(this.root.querySelectorAll('product-form form[data-type="add-to-cart-form"]')).filter(form => form.getAttribute('id') === 'product-form-' + this.dataset.sectionId);
      this.form = forms.length === 1 ? forms[0] : null;
      const submit = this.form?.querySelector('button[type="submit"][name="add"]');
      if (submit !== this.submit) {
        this.intersection?.disconnect();
        this.submit = submit;
        this.nativeVisible = false;
        if (submit && typeof IntersectionObserver === 'function') {
          this.intersection = new IntersectionObserver(entries => {
            this.nativeVisible = entries.some(entry => entry.isIntersecting);
            this.visibility();
          });
          this.intersection.observe(submit);
        }
      }
    }

    state() {
      const variant = this.variant();
      const groups = this.groups();
      const values = groups.map(group => this.value(group));
      const explicit = groups.every((group, index) => {
        const count = group.querySelectorAll('input[type="radio"], select option').length;
        return count === 1 || this.confirmed.get(index) === values[index] || String(variant?.id) === this.explicitURL;
      });
      const exact = !groups.length || (Array.isArray(variant?.options) && variant.options.length === values.length && variant.options.every((value, index) => String(value) === values[index]));
      const ids = this.form ? Array.from(this.form.elements).filter(input => input.name === 'id') : [];
      const matches = ids.length === 1 && String(ids[0].value) === String(variant?.id);
      const pending = Boolean(this.root.pendingRequestUrl) || (Boolean(variant) && (!exact || (ids.length === 1 && !matches)));
      return { variant, groups, values, explicit, pending, ready: Boolean(variant?.available) && exact && matches && explicit && !pending && !ids[0]?.disabled };
    }

    payload(state) {
      this.reviewTarget = null;
      const fallback = target => { this.reviewTarget = target; return null; };
      if (!this.form || this.appTouched || this.root.querySelector('recipient-form')) return fallback(this.root.querySelector(appOptions) || this.form);
      const controls = Array.from(this.form.elements);
      const result = { id: String(state.variant?.id || ''), quantity: 1 };
      const seen = new Set();
      const properties = Object.create(null);
      for (const input of controls) {
        if (!input.matches('input, select, textarea') || input.disabled || input.matches(':disabled') || ['submit', 'button', 'reset'].includes(input.type)) continue;
        if (!input.name) {
          if (input.closest('.essential-upsell-addon-container')) continue;
          return fallback(input);
        }
        if (['radio', 'checkbox'].includes(input.type) && !input.checked) continue;
        if (input.closest('variant-selects')) continue;
        const name = input.name;
        if (['form_type', 'utf8', 'product-id', 'section-id', 'return_to'].includes(name)) continue;
        if (input.type === 'file' || input.multiple || input.hasAttribute('multiple')) return fallback(input);
        if (seen.has(name)) return fallback(input);
        seen.add(name);
        if (name === 'id') { if (String(input.value) !== result.id) return fallback(input); continue; }
        if (name === 'quantity') {
          const quantity = Number(input.value);
          if (!Number.isSafeInteger(quantity) || quantity < 1 || (input.checkValidity && !input.checkValidity())) return fallback(input);
          result.quantity = quantity;
          continue;
        }
        if (name === 'selling_plan') {
          if (input.value && !/^\d+$/.test(input.value)) return fallback(input);
          if (input.value) result.selling_plan = String(input.value);
          continue;
        }
        const property = name.match(/^properties\[([^\[\]]+)\]$/);
        if (property) { properties[property[1]] = String(input.value); continue; }
        return fallback(input);
      }
      if (this.dataset.requiresSellingPlan === 'true' && !result.selling_plan) return fallback(this.form);
      if (!seen.has('id')) return null;
      if (Object.keys(properties).length) result.properties = properties;
      return result;
    }

    sync() {
      this.bindForm();
      const state = this.state();
      this.currentState = state;
      const hasSize = Number(this.dataset.sizeIndex) >= 0;
      const choose = hasSize ? 'Choose size' : this.dataset.labelOptions;
      const nativeBusy = this.submit?.classList.contains('loading') || this.submit?.getAttribute('aria-disabled') === 'true';
      const payload = state.ready ? this.payload(state) : null;
      this.mode = this.uncertain || this.added ? 'cart' : !state.explicit || state.values.some(value => !value) ? 'choose' : state.pending ? 'pending' : !state.variant?.available ? 'unavailable' : !payload || this.submit?.disabled || !this.form ? 'review' : 'add';
      const labels = { cart: 'View cart', choose, pending: 'Updating…', unavailable: hasSize ? 'Choose another size' : this.dataset.labelOptions, review: 'Review options', add: this.dataset.labelAdd };
      writeText(this.action, this.busy || nativeBusy ? 'Adding…' : labels[this.mode]);
      this.action.disabled = Boolean(this.busy || nativeBusy || this.mode === 'pending');
      this.action.setAttribute('aria-busy', String(Boolean(this.busy)));
      let summary = state.explicit ? state.values.filter(Boolean).join(' / ') || 'Selected item' : choose;
      if (state.explicit && state.variant?.available === false) summary += ' · ' + this.dataset.labelUnavailable;
      if (payload?.quantity > 1) summary += ' · Qty ' + payload.quantity;
      writeText(this.choice, summary);
      const priceRoot = this.root.querySelector('[id^="price-"] .price');
      const priceSelector = priceRoot?.classList.contains('price--on-sale') ? '.price__sale .price-item--sale' : '.price__regular .price-item--regular';
      const nativePrice = priceRoot?.querySelector(priceSelector)?.textContent.replace(/\s+/g, ' ').trim() || '';
      writeText(this.price, state.pending ? '' : nativePrice);
      const feedback = this.uncertain ? 'We couldn’t confirm the result. Check your cart before adding again.' : this.busy ? 'Adding your selection…' : this.error || (this.added ? 'Added to cart.' : this.mode === 'review' ? 'Review your selected options in the product form.' : '');
      writeText(this.status, feedback);
      this.visibility();
    }

    visible(node) {
      if (!node || node.closest('[hidden]')) return false;
      const style = getComputedStyle(node);
      const rect = node.getBoundingClientRect();
      return style.display !== 'none' && style.visibility !== 'hidden' && Number(style.opacity) !== 0 && rect.width > 0 && rect.height > 0 && rect.bottom > 0 && rect.top < window.innerHeight;
    }

    visibility() {
      if (!this.bar) return;
      const modal = document.querySelector('dialog[open], cart-drawer.active, details[open].menu-opening, [aria-modal="true"][open]');
      const conflicting = Array.from(document.querySelectorAll(inheritedSticky)).find(node => !this.contains(node) && this.visible(node));
      if (conflicting) this.dataset.stickyConflict = conflicting.id || conflicting.tagName.toLowerCase();
      else delete this.dataset.stickyConflict;
      const focused = this.bar.contains(document.activeElement);
      const hidden = !this.mobile.matches || !this.form || Boolean(modal || conflicting) || (this.nativeVisible && !focused && !this.busy && !this.uncertain && !this.error);
      if (this.bar.hidden !== hidden) this.bar.hidden = hidden;
      this.toggleAttribute('data-visible', !hidden);
    }

    review() {
      const state = this.state();
      const group = state.groups.find((item, index) => this.confirmed.get(index) !== this.value(item)) || state.groups[Number(this.dataset.sizeIndex)] || state.groups[0];
      const invalid = this.form && Array.from(this.form.elements).find(input => input.willValidate && input.validity?.valid === false);
      const target = this.mode === 'review' ? invalid || this.reviewTarget || this.submit : group?.querySelector('input:checked:not(:disabled), select:not(:disabled), input:not(:disabled)') || this.submit;
      target?.scrollIntoView({ block: 'center', behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'instant' : 'smooth' });
      target?.focus({ preventScroll: true });
      if (this.mode === 'review') this.form?.reportValidity?.();
    }

    savePending(value) {
      try { if (value) sessionStorage.setItem(this.storageKey, String(value)); else sessionStorage.removeItem(this.storageKey); } catch (_) {}
    }

    async activate() {
      this.sync();
      if (this.action.disabled || this.busy) return;
      if (this.mode === 'cart') { window.location.assign(this.dataset.cartUrl); return; }
      if (this.mode !== 'add') { this.review(); return; }
      const state = this.state();
      const item = state.ready && this.payload(state);
      if (!item || !this.form.checkValidity()) { this.review(); return; }
      this.busy = true;
      this.error = '';
      this.savePending(item.id);
      this.sync();
      const cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
      let sections = [];
      try { sections = cart?.getSectionsToRender?.().map(section => section.id).slice(0, 5) || []; } catch (_) {}
      const body = { items: [item] };
      if (sections.length) { body.sections = sections; body.sections_url = window.location.pathname; }
      const controller = new AbortController();
      const timer = setTimeout(() => controller.abort(), 15000);
      try {
        const response = await fetch(this.dataset.addUrl, { method: 'POST', credentials: 'same-origin', headers: { 'Content-Type': 'application/json', Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' }, body: JSON.stringify(body), signal: controller.signal });
        let data;
        try { data = await response.json(); } catch (_) { throw new Error('Unconfirmed cart result'); }
        if (!response.ok) {
          if (response.status === 422 || response.status === 400) {
            this.error = typeof data.description === 'string' ? data.description : 'This selection couldn’t be added. Please review your options.';
            this.savePending(false);
            return;
          }
          throw new Error('Unconfirmed cart result');
        }
        const added = Array.isArray(data.items) ? data.items : [data];
        if (added.length !== 1 || String(added[0]?.variant_id || added[0]?.id) !== item.id || !(Number(added[0]?.quantity) >= item.quantity)) throw new Error('Unexpected cart result');
        this.added = true;
        this.savePending(false);
        const renderData = { ...data, id: added[0].id, key: added[0].key };
        try {
          if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') publish(PUB_SUB_EVENTS.cartUpdate, { source: 'fs-live-purchase', productVariantId: item.id, cartData: renderData });
          if (cart && sections.length && sections.every(id => typeof data.sections?.[id] === 'string') && typeof cart.renderContents === 'function') {
            cart.setActiveElement?.(this.action);
            cart.classList.remove('is-empty');
            cart.renderContents(renderData);
          }
        } catch (_) { this.error = 'Added to cart. Open your cart to review it.'; }
      } catch (_) {
        this.uncertain = true;
      } finally {
        clearTimeout(timer);
        this.busy = false;
        if (this.connected) this.sync();
      }
    }
  }
  customElements.define('fs-live-purchase', FsLivePurchase);
})();
